/**
 * Parses a pure `YYYY-MM-DD` calendar-date string (e.g.
 * `TimeOffRequestDayDto.date`) into a *local* midnight `Date` by reading its
 * numeric parts directly — never via `new Date(dateStr)` or `parseApiDate`,
 * both of which treat a bare date string as UTC midnight and would silently
 * shift the displayed day back by one in any negative-offset timezone. This
 * field has no time-of-day/instant semantics, so there is no UTC conversion
 * to do in the first place.
 */
function parseLocalCalendarDate(dateStr: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateStr);
  if (!match) return null;
  const [, year, month, day] = match;
  return new Date(Number(year), Number(month) - 1, Number(day));
}

/**
 * Formats a set of `YYYY-MM-DD` dates as a compact range: `"24 mar"` for a
 * single day, `"15 – 17 mar"` within the same month, `"29 mar – 2 abr"` across
 * months. Pass `includeYear` to append it (`"15 – 17 mar 2026"`), used by
 * "Mis solicitudes" — the calendar overview bar omits the year.
 */
export function formatDayRange(dates: string[], options?: { includeYear?: boolean }): string {
  const sorted = [...dates].sort();
  const first = parseLocalCalendarDate(sorted[0]);
  const last = parseLocalCalendarDate(sorted[sorted.length - 1]);
  if (!first || !last) return '';

  const sameMonth =
    first.getMonth() === last.getMonth() && first.getFullYear() === last.getFullYear();
  const dayOnly = (d: Date) => new Intl.DateTimeFormat('es', { day: 'numeric' }).format(d);
  const dayMonth = (d: Date) =>
    new Intl.DateTimeFormat('es', { day: 'numeric', month: 'short' }).format(d);
  const year = options?.includeYear ? ` ${first.getFullYear()}` : '';

  if (sorted.length === 1) return `${dayMonth(first)}${year}`;
  if (sameMonth) return `${dayOnly(first)} – ${dayMonth(last)}${year}`;
  return `${dayMonth(first)} – ${dayMonth(last)}${year}`;
}

/** `"1 de agosto de 2020"` — long-form display date, locale-aware. */
export function formatLongDate(dateStr: string): string {
  const date = parseLocalCalendarDate(dateStr);
  if (!date) return '';
  return new Intl.DateTimeFormat('es', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date);
}
